/**
 * `useBulkUnassignApprovalChain` mutation hook.
 *
 * Wraps the raw `bulkUnassignApprovalChain` service fn (per AD-23). Invalidations
 * mirror `useBulkAssignApprovalChain`. Returns `{ removed, skipped, total }` so the
 * caller can report counts. Caller owns error UX.
 *
 * Reuses the `asset_workflow_removed` analytics event (a bulk unassign is still
 * a removal from the user's perspective); no per-asset fan-out of events.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { ASSET_APPROVAL_CHAIN_QUERY_KEYS } from '@/entities/asset-approval-chain';
import { PROJECT_FILE_QUERY_KEYS } from '@/entities/project-file';
import { PROJECT_FOLDER_QUERY_KEYS } from '@/entities/project-folder';
import { APPROVAL_CHAIN_QUERY_KEYS } from '@/entities/approval-chain';
import { track } from '@/shared/analytics';

import { bulkUnassignApprovalChain } from './asset-approval-chain.mutations.api';
import { invalidateFolderAggregates } from './invalidate-folder-aggregates';

export { bulkUnassignApprovalChain };

interface UseBulkUnassignApprovalChainOptions {
  projectId: string;
  /** Folder currently in view — used to refresh its files list after removing. */
  folderId?: string;
}

interface BulkUnassignApprovalChainVars {
  approvalChainId: string;
  assetIds: string[];
}

export function useBulkUnassignApprovalChain(options: UseBulkUnassignApprovalChainOptions) {
  const queryClient = useQueryClient();
  const { projectId, folderId } = options;

  return useMutation({
    mutationFn: ({ approvalChainId, assetIds }: BulkUnassignApprovalChainVars) =>
      bulkUnassignApprovalChain(projectId, approvalChainId, assetIds),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: APPROVAL_CHAIN_QUERY_KEYS.all(projectId),
      });
      void queryClient.invalidateQueries({
        queryKey: ASSET_APPROVAL_CHAIN_QUERY_KEYS.namespace(projectId),
      });
      void queryClient.invalidateQueries({
        queryKey: PROJECT_FILE_QUERY_KEYS.details(projectId),
      });
      if (folderId) {
        void queryClient.invalidateQueries({
          queryKey: PROJECT_FOLDER_QUERY_KEYS.files(projectId, folderId),
        });
      }
      invalidateFolderAggregates(queryClient, projectId, folderId);
      track('asset_workflow_removed', { project_id: projectId });
    },
  });
}
